// Client-side validation uses the same request schemas as the API (`@lafayette/schemas`), so the form
// refuses what the server would refuse. A schema issue is technical; this module turns it into a sentence
// a staff member can act on and attaches it to a field.

export type FieldErrors = Readonly<Record<string, string>>;

/** The part of a zod issue this module reads. The admin imports no zod: the shape is structural. */
export type SchemaIssue = Readonly<{
  code: string;
  path: readonly PropertyKey[];
  message: string;
  origin?: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
  format?: string;
}>;

export function describeIssue(issue: SchemaIssue): string {
  switch (issue.code) {
    case "too_small":
      if (issue.origin === "string") {
        return Number(issue.minimum) <= 1
          ? "Enter a value."
          : `Use at least ${issue.minimum} characters.`;
      }
      if (issue.origin === "array") return `Choose at least ${issue.minimum}.`;
      return `Use ${issue.minimum} or more.`;
    case "too_big":
      if (issue.origin === "string") return `Use at most ${issue.maximum} characters.`;
      if (issue.origin === "array") return `Choose at most ${issue.maximum}.`;
      return `Use ${issue.maximum} or less.`;
    case "invalid_format":
      return issue.format === "datetime"
        ? "Enter a valid date and time."
        : "Use the allowed format.";
    case "invalid_value":
      return "Choose one of the listed values.";
    case "invalid_type":
      return "Enter a value.";
    default:
      return issue.message;
  }
}

/**
 * One message per field. `resolve` maps an issue path to the id of a form field, or `null` for an issue
 * that belongs to no field (it goes under the `""` key, the form-level message). The first issue of a
 * field wins.
 */
export function issuesToFieldErrors(
  issues: readonly SchemaIssue[],
  resolve: (path: readonly PropertyKey[]) => string | null,
): FieldErrors {
  const errors: Record<string, string> = {};
  for (const issue of issues) {
    const field = resolve(issue.path) ?? "";
    if (errors[field] === undefined) errors[field] = describeIssue(issue);
  }
  return errors;
}

/** The ids of the invalid fields, in the order of `order` (the order on the page), for the error summary. */
export function orderedErrorFields(
  errors: FieldErrors,
  order: readonly string[],
): readonly string[] {
  const known = order.filter((field) => errors[field] !== undefined);
  const unknown = Object.keys(errors).filter((field) => field !== "" && !order.includes(field));
  return [...known, ...unknown];
}
